import type { FolderSchema, TableSidebarTree } from '../api/contracts'

/** `folderId` is `ancestorId` or sits somewhere inside it. */
export function isWithinFolder(
  folders: readonly FolderSchema[],
  folderId: string,
  ancestorId: string
): boolean {
  const parentById = new Map(folders.map((folder) => [folder.id, folder.parent_id]))
  const seen = new Set<string>()
  let current: string | null = folderId
  while (current !== null && !seen.has(current)) {
    if (current === ancestorId) return true
    seen.add(current)
    current = parentById.get(current) ?? null
  }
  return false
}

/**
 * A folder can move into `parentId` (a folder, or `null` for the top level):
 * not where it already is, and not into itself or a folder inside it.
 */
export function canMoveFolderTo(
  folders: readonly FolderSchema[],
  folderId: string,
  parentId: string | null
): boolean {
  const folder = folders.find((candidate) => candidate.id === folderId)
  if (!folder || folder.parent_id === parentId) return false
  return parentId === null || !isWithinFolder(folders, parentId, folderId)
}

/**
 * Moves a folder, with everything in it, into another folder or to the top
 * level, after the folders already there; the folders it left close the gap.
 * A move it can't make leaves the arrangement as it was.
 */
export function moveFolderToContainer(
  organization: TableSidebarTree,
  folderId: string,
  parentId: string | null
): TableSidebarTree {
  if (!canMoveFolderTo(organization.folders, folderId, parentId)) return organization
  const oldParentId = organization.folders.find((folder) => folder.id === folderId)!.parent_id
  const newSiblingOrders = organization.folders
    .filter((folder) => folder.parent_id === parentId)
    .map((folder) => folder.sort_order)
  const moved = organization.folders.map((folder) =>
    folder.id === folderId
      ? {
          ...folder,
          parent_id: parentId,
          sort_order: newSiblingOrders.length > 0 ? Math.max(...newSiblingOrders) + 1 : 0,
        }
      : folder
  )
  const oldSiblingIds = moved
    .filter((folder) => folder.parent_id === oldParentId)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((folder) => folder.id)
  return {
    ...organization,
    folders: moved.map((folder) => {
      const index = oldSiblingIds.indexOf(folder.id)
      return index >= 0 ? { ...folder, sort_order: index } : folder
    }),
  }
}

/**
 * Removes a folder but nothing in it: its folders take its place among its
 * siblings, in their order, and its tables join the end of its parent's (the
 * top level's, for a top-level folder). An unknown folder leaves the
 * arrangement as it was.
 */
export function deleteFolderKeepingContents(
  organization: TableSidebarTree,
  folderId: string
): TableSidebarTree {
  const deleted = organization.folders.find((folder) => folder.id === folderId)
  if (!deleted) return organization
  const parentId = deleted.parent_id
  const bySortOrder = (a: FolderSchema, b: FolderSchema) => a.sort_order - b.sort_order
  const children = organization.folders.filter((folder) => folder.parent_id === folderId)
  children.sort(bySortOrder)
  const siblings = organization.folders.filter((folder) => folder.parent_id === parentId)
  siblings.sort(bySortOrder)
  // The deleted folder's place among its siblings goes to its own folders.
  const newSiblingIds = siblings.flatMap((folder) =>
    folder.id === folderId ? children.map((child) => child.id) : [folder.id]
  )
  const movedTables = [...deleted.tables].sort((a, b) => a.sort_order - b.sort_order)

  const folders = organization.folders
    .filter((folder) => folder.id !== folderId)
    .map((folder) => {
      const siblingIndex = newSiblingIds.indexOf(folder.id)
      let next =
        siblingIndex >= 0 ? { ...folder, parent_id: parentId, sort_order: siblingIndex } : folder
      if (parentId !== null && folder.id === parentId) {
        const start = folder.tables.length
        next = {
          ...next,
          tables: [
            ...folder.tables,
            ...movedTables.map((table, index) => ({
              table_id: table.table_id,
              sort_order: start + index,
            })),
          ],
        }
      }
      return next
    })

  const movedIds = movedTables.map((table) => table.table_id)
  return {
    ...organization,
    folders,
    unorganized_table_order:
      parentId === null
        ? [
            ...organization.unorganized_table_order.filter((id) => !movedIds.includes(id)),
            ...movedIds,
          ]
        : organization.unorganized_table_order,
  }
}
